import { createArcadePrizes } from './188-arcade-prizes.mjs';

/** Original rendered8-frame jewel VFX. Three short sprite instances at most;
 * no simulation, score or frame loop lives here. Late decode cannot revive a
 * cancelled reward, and unavailable artwork leaves the numeric toast intact. */
export function createMarbleRewards(ctx, playfield) {
  const prizes = createArcadePrizes(ctx, playfield);
  const nodes = new Set(),
    animations = new Set();
  let loading = null,
    generation = 0,
    disposed = false;
  function prepare() {
    prizes.prepare();
    if (loading || typeof Image === 'undefined') return loading;
    const image = new Image();
    image.src = new URL('../assets/game-marble-burst-v1.webp', import.meta.url).href;
    if (typeof image.decode !== 'function') return (loading = Promise.resolve(false));
    loading = image.decode().then(
      () => true,
      () => false,
    );
    return loading;
  }
  function cancel() {
    generation++;
    prizes.cancel();
    for (const animation of animations) animation.cancel();
    animations.clear();
    for (const node of nodes) node.remove();
    nodes.clear();
  }
  function animate(node, frames, options) {
    const animation = node.animate(frames, options);
    animations.add(animation);
    void animation.finished.then(
      () => animations.delete(animation),
      () => animations.delete(animation),
    );
    return animation;
  }
  function play({ finale = false } = {}) {
    cancel();
    prizes.play({ finale });
    const token = generation;
    const ready = prepare();
    if (!ready) return;
    void ready.then((decoded) => {
      if (
        !decoded ||
        disposed ||
        token !== generation ||
        document.hidden ||
        !playfield.isConnected ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      )
        return;
      for (let i = 0; i < (finale ? 3 : 1); i++) {
        const node = ctx.e('span', 'marble-reward-sprite');
        node.setAttribute('aria-hidden', 'true');
        node.dataset.satellite = String(i > 0);
        node.style.left = `${i === 0 ? 50 : i === 1 ? 24 : 76}%`;
        node.style.top = `${i === 0 ? 45 : i === 1 ? 25 : 64}%`;
        playfield.append(node);
        nodes.add(node);
        if (typeof node.animate !== 'function') {
          node.remove();
          nodes.delete(node);
          continue;
        }
        const frames = Array.from({ length: 9 }, (_, frame) => {
          const index = Math.min(frame, 7);
          return {
            backgroundPosition: `${((index % 4) * 100) / 3}% ${index < 4 ? 0 : 100}%`,
            offset: frame / 8,
            easing: 'steps(1, end)',
          };
        });
        const film = animate(node, frames, {
          duration: 1000,
          delay: i * 140,
          easing: 'linear',
          fill: 'both',
        });
        animate(
          node,
          [
            { opacity: 0, transform: 'translate(-50%, -50%) scale(.8)' },
            { opacity: 1, transform: 'translate(-50%, -50%) scale(1)', offset: 0.16 },
            { opacity: 1, transform: 'translate(-50%, -50%) scale(1.05)', offset: 0.58 },
            { opacity: 0, transform: 'translate(-50%, -50%) scale(1.12)' },
          ],
          { duration: 1050, delay: i * 140, easing: 'ease-out', fill: 'both' },
        );
        void film.finished.then(
          () => {
            node.remove();
            nodes.delete(node);
          },
          () => {},
        );
      }
    });
  }
  return {
    prepare,
    play,
    cancel,
    dispose() {
      disposed = true;
      prizes.dispose();
      cancel();
      loading = null;
    },
  };
}
